/**
 * Insurance API Service
 * Handles all API calls to the backend insurance service
 */

import {VehicleRow} from '../types';

// Use environment variable for API URL, fallback to relative path for development
const API_BASE_URL = process.env.REACT_APP_API_URL 
  ? `${process.env.REACT_APP_API_URL}/api/v1/insurance`
  : '/api/v1/insurance';

/**
 * Download the Excel template
 */
export const downloadTemplate = async (): Promise<Blob> => {
  const response = await fetch(`${API_BASE_URL}/template?includeSample=false`);
  if (!response.ok) {
    throw new Error('Αποτυχία λήψης προτύπου');
  }
  return response.blob();
};

export interface ProcessingStatus {
  processed: number;
  total: number;
  percentage: number;
  completed: boolean;
  cancelled: boolean;
  error?: string;
  elapsedTimeMs: number;
}

/**
 * Start processing Excel file with vehicle data - returns jobId
 */
export const startProcessingExcelFile = async (file: Blob, delayMs: number = 300): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file, 'vehicles.xlsx');
  formData.append('delayMs', delayMs.toString());

  const response = await fetch(`${API_BASE_URL}/process-excel`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Αποτυχία έναρξης επεξεργασίας αρχείου');
  }

  const data = await response.json();
  return data.jobId;
};

/**
 * Get processing status for a job
 */
export const getProcessingStatus = async (jobId: string): Promise<ProcessingStatus> => {
  const response = await fetch(`${API_BASE_URL}/process-status?jobId=${encodeURIComponent(jobId)}`);
  
  if (!response.ok) {
    throw new Error('Αποτυχία λήψης κατάστασης');
  }

  return response.json();
};

/**
 * Cancel a processing job
 */
export const cancelProcessing = async (jobId: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/cancel?jobId=${encodeURIComponent(jobId)}`, {
    method: 'POST',
  });

  if (!response.ok) {
    throw new Error('Αποτυχία ακύρωσης');
  }
};

/**
 * Download the processed Excel file result
 */
export const downloadProcessedFile = async (jobId: string): Promise<Blob> => {
  const response = await fetch(`${API_BASE_URL}/download-result?jobId=${encodeURIComponent(jobId)}`);

  if (!response.ok) {
    let errorMessage = 'Αποτυχία λήψης αποτελεσμάτων';
    
    if (response.status === 404) {
      errorMessage = 'Δεν βρέθηκε το αρχείο αποτελεσμάτων. Η εργασία μπορεί να έχει λήξει ή να μην υπάρχει.';
    } else if (response.status === 202) {
      errorMessage = 'Η επεξεργασία δεν έχει ολοκληρωθεί ακόμα. Παρακαλώ περιμένετε.';
    } else if (response.status === 410) {
      errorMessage = 'Η εργασία ακυρώθηκε. Δεν υπάρχουν αποτελέσματα προς λήψη.';
    }
    
    throw new Error(errorMessage);
  }

  // Check if response is actually a blob (Excel file)
  const contentType = response.headers.get('content-type');
  if (contentType && 
      !contentType.includes('application/octet-stream') && 
      !contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
    // If content type is unexpected, try to read as text for error message
    try {
      const text = await response.clone().text();
      throw new Error(text || 'Αναπάντεχο τύπος αρχείου');
    } catch {
      throw new Error('Αναπάντεχο τύπος αρχείου');
    }
  }

  return response.blob();
};

/**
 * Check insurance for a single vehicle
 */
export const checkSingleVehicle = async (
  vatNumber: string,
  licensePlate: string
): Promise<VehicleRow> => {
  const response = await fetch(`${API_BASE_URL}/check`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      vatNumber,
      licensePlate,
    }),
  });

  if (!response.ok) {
    throw new Error('Αποτυχία ελέγχου οχήματος');
  }

  return response.json();
};

/**
 * Health check endpoint
 */
export const healthCheck = async (): Promise<{ status: string }> => {
  const response = await fetch(`${API_BASE_URL}/health`);
  if (!response.ok) {
    throw new Error('Αποτυχία ελέγχου υγείας');
  }
  return response.json();
};
